import { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Video, Shield, MapPin, Bell, MoreHorizontal, X,
  Home, Phone, ClipboardList, Settings, Building2,
  User, LogOut, Sun, Activity, Monitor, Tablet,
  ChevronRight, Check, ShieldCheck
} from 'lucide-react';
import useAuthStore from '../stores/authStore';
import fetchWithTenant from '../utils/fetchWithTenant';
import { useBranding } from '../contexts/BrandingContext';

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout, tenants, currentTenant, switchTenant } = useAuthStore();
  const { refreshBranding } = useBranding();
  const [showMore, setShowMore] = useState(false);
  const [showSitePicker, setShowSitePicker] = useState(false);
  const longPressTimer = useRef(null);

  // Notification badge polling
  const [notificationCount, setNotificationCount] = useState(0);

  const fetchNotificationCount = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/notifications/stats');
      if (res.ok) {
        const data = await res.json();
        setNotificationCount(data.unresolved || 0);
      }
    } catch (e) { /* non-critical */ }
  }, []);

  useEffect(() => {
    if (user && (user.role === 'ADMIN' || user.role === 'admin' || user.isPlatformAdmin)) {
      fetchNotificationCount();
      const interval = setInterval(fetchNotificationCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotificationCount, currentTenant?.id]);

  // ─── Bottom bar tabs ──────────────────────────
  const bottomTabs = [
    { id: 'home',    path: '/',              icon: Home,    label: 'Home' },
    { id: 'cameras', path: '/cameras',       icon: Video,   label: 'Cameras' },
    { id: 'doors',   path: '/access',        icon: Shield,  label: 'Doors' },
    { id: 'alerts',  path: '/notifications', icon: Bell,    label: 'Alerts' },
    { id: 'more',    icon: MoreHorizontal,   label: 'More' },
  ];

  // ─── More drawer items ────────────────────────
  const moreItems = [
    {
      id: 'nav',
      label: null,
      items: [
        { path: '/map',          icon: MapPin,        label: 'Site Map',            show: true },
        { path: '/intercoms',    icon: Phone,         label: 'Intercoms',           show: !!user?.canAnswerIntercom },
        { path: '/registration', icon: ClipboardList, label: 'Registration',        show: true },
        { path: '/shabbat',      icon: Sun,           label: 'Sabbath Mode',        show: !!user?.canActivateShabbat },
      ]
    },
    {
      id: 'modules',
      label: 'Modules',
      items: [
        { path: '/recording-assurance',       icon: ShieldCheck, label: 'Recording Assurance', show: isAdmin() },
        { path: '/monitoring',                icon: Activity,    label: 'CCM Monitor',         show: isAdmin() },
        { path: '/monitoring/devices',        icon: Monitor,     label: 'Devices',             show: isAdmin() },
        { path: '/monitoring/operations',     icon: Settings,    label: 'Monitor Settings',     show: isAdmin() },
      ]
    },
    {
      id: 'system',
      label: 'System',
      items: [
        { path: '/admin',    icon: Settings,  label: 'Settings',   show: isAdmin() },
        { path: '/profile',  icon: User,      label: 'Profile',    show: true },
        { path: '/platform', icon: Building2, label: 'Platform',   show: !!user?.isPlatformAdmin, badge: 'Staff' },
      ]
    },
  ];

  // Don't render on login/register pages
  if (['/login', '/register', '/verify-email', '/forgot-password', '/reset-password', '/registration-pending'].includes(location.pathname)) {
    return null;
  }

  // Check if "More" items contain active route
  const isMoreActive = moreItems.some(section =>
    section.items.some(item => item.show && item.path !== '/' && location.pathname.startsWith(item.path))
  ) || (location.pathname === '/' && !bottomTabs.some(t => t.path === '/'));

  const handleNavClick = (navPath) => {
    setShowMore(false);
    setShowSitePicker(false);
    navigate(navPath);
  };

  const handleLogout = async () => {
    setShowMore(false);
    await logout();
    navigate('/login');
  };

  // Long-press handlers for site switching
  const startLongPress = () => {
    if (tenants.length <= 1) return;
    longPressTimer.current = setTimeout(() => {
      setShowSitePicker(true);
    }, 500);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  return (
    <>
      {/* ─── More Drawer ─── */}
      {showMore && (
        <>
          <div className="more-drawer-backdrop" onClick={() => setShowMore(false)} />
          <div className="more-drawer">
            {/* Header - user info + site switcher */}
            <div className="more-drawer-header">
              <div className="more-drawer-user">
                <div className="more-drawer-avatar">
                  {(user?.name || user?.username || 'U').charAt(0).toUpperCase()}
                </div>
                <div className="more-drawer-user-info">
                  <span className="more-drawer-name">{user?.name || user?.username}</span>
                  <span className="more-drawer-role">
                    {user?.isPlatformAdmin ? 'Platform Admin' : user?.role}
                  </span>
                </div>
              </div>
              <button className="more-drawer-close" onClick={() => setShowMore(false)}>
                <X size={20} />
              </button>
            </div>

            {/* Site switcher bar (only if multi-tenant) */}
            {tenants.length > 1 && currentTenant && (
              <button
                className="more-drawer-site-bar"
                onClick={() => setShowSitePicker(true)}
              >
                <Building2 size={16} />
                <span className="more-drawer-site-name">{currentTenant.name}</span>
                <ChevronRight size={16} className="more-drawer-site-chevron" />
              </button>
            )}

            {/* Nav sections */}
            <div className="more-drawer-content">
              {moreItems.map((section) => {
                const visible = section.items.filter(item => item.show);
                if (visible.length === 0) return null;
                return (
                  <div key={section.id} className="more-drawer-section">
                    {section.label && (
                      <div className="more-drawer-section-label">{section.label}</div>
                    )}
                    {visible.map((item) => (
                      <button
                        key={item.path}
                        className={`more-drawer-item ${location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path)) ? 'active' : ''}`}
                        onClick={() => handleNavClick(item.path)}
                      >
                        <item.icon size={20} />
                        <span>{item.label}</span>
                        {item.badge && (
                          <span className="more-drawer-badge">{item.badge}</span>
                        )}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Sign out */}
            <div className="more-drawer-footer">
              <button className="more-drawer-item more-drawer-logout" onClick={handleLogout}>
                <LogOut size={20} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* ─── Bottom Nav Bar ─── */}
      <nav className="bottom-nav">
        {bottomTabs.map((tab) => {
          if (tab.id === 'more') {
            return (
              <button
                key="more"
                className={`bottom-nav-item ${showMore || isMoreActive ? 'active' : ''}`}
                onClick={() => { setShowMore(!showMore); setShowSitePicker(false); }}
              >
                <div className="bottom-nav-icon">
                  {showMore ? <X size={22} /> : <tab.icon size={22} />}
                </div>
                <span>{showMore ? 'Close' : tab.label}</span>
              </button>
            );
          }
          return (
            <NavLink
              key={tab.id}
              to={tab.path}
              end={tab.path === '/'}
              className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}
              onClick={() => { setShowMore(false); setShowSitePicker(false); }}
              onTouchStart={tab.id === 'home' ? startLongPress : undefined}
              onTouchEnd={tab.id === 'home' ? cancelLongPress : undefined}
              onTouchMove={tab.id === 'home' ? cancelLongPress : undefined}
            >
              <div className="bottom-nav-icon">
                <tab.icon size={22} />
              </div>
              <span>{tab.label}</span>
              {tab.id === 'alerts' && notificationCount > 0 && (
                <span className="bottom-nav-badge">
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* ─── Site Picker Bottom Sheet ─── */}
      {showSitePicker && (
        <div
          className="site-picker-backdrop"
          onClick={() => setShowSitePicker(false)}
        >
          <div
            className="site-picker-sheet"
            onClick={e => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="site-picker-handle">
              <div className="site-picker-handle-bar" />
            </div>
            <div className="site-picker-header">
              <p className="site-picker-title">Switch Site</p>
              <p className="site-picker-subtitle">Select a site to manage</p>
            </div>
            <div className="site-picker-list">
              {tenants.map(tenant => (
                <button
                  key={tenant.id}
                  className={`site-picker-option ${tenant.id === currentTenant?.id ? 'active' : ''}`}
                  onClick={async () => {
                    if (switchTenant && tenant.id !== currentTenant?.id) {
                      await switchTenant(tenant.id);
                      const orgId = useAuthStore.getState().currentTenant?.organizationId;
                      if (orgId) {
                        try { await refreshBranding(orgId); } catch {}
                      }
                    }
                    setShowSitePicker(false);
                    setShowMore(false);
                  }}
                >
                  <Building2 size={20} />
                  <div className="site-picker-option-info">
                    <span className="site-picker-option-name">{tenant.name}</span>
                    {tenant.id === currentTenant?.id && (
                      <span className="site-picker-option-current">Current site</span>
                    )}
                  </div>
                  {tenant.id === currentTenant?.id && <Check size={16} />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNav;
